import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './TransferItemsForm.module.scss'
import { IExtract, OrderExtract, OrderItemExtract } from '../../../../modules/consume/domain/models/Extract';
import { TransferItemsFormItem } from './TransferItemsFormItem';
import { Button, CircularProgress } from '@mui/material';
import ModalLayout from '../../../../components/ui/modalLayout/ModalLayout';
import { ProductType } from '../../../../modules/catalog/domain/models/ProductType';
import UseTable from '../../../../modules/consume/presentation/UseTable';
import { ITable } from '../../../../modules/consume/domain/models/ITables';
import { TransferItemsFormTable } from './TransferItemsFormTable';
import useOperatorStore from '../../../../modules/operator/store/OperatorStore';
import { OperatorPermissions } from '../../../../modules/operator/domains/models/Permissions';
import { useI18n } from '../../../../hooks/useI18n';

export enum TransferItemsStep {
    Items = 1,
    Table = 2
}

export interface ITransferItemSelectable extends OrderItemExtract {
    isChecked: boolean;
}

export interface ITransferItemsFormValue extends OrderExtract {
    items: ITransferItemSelectable[];
}

export interface ITransferItemsFormProps {
    extract: IExtract;
    onClose: () => void;
    onSubmit: (orderItems: ITransferItemSelectable[], toTag: string, annotation?: string) => Promise<void>
    currentTable?: string;
}

const TransferItemsForm: FC<ITransferItemsFormProps> = ({ extract, onClose, onSubmit, currentTable }) => {
    const { t } = useI18n();
    const { isLoading, tableList: getTableList, getLocalTableList } = UseTable();
    const [currentOperator, hasPermission] = useOperatorStore((state) => ([state.currentOperator, state.hasPermission]));

    const [currentStep, setCurrentStep] = useState(TransferItemsStep.Items);
    const [selectableOrder, setSelectableOrder] = useState<ITransferItemsFormValue[]>([]);
    const [observation, setObservation] = useState<string>('');

    const [selectedTable, setSelectedTable] = useState<ITable>();

    const divRef = useRef<HTMLDivElement>(null);

    const handleChangeObservation = useCallback((value: string) => {
        setObservation(value);
    }, []);   

    const extractToSelectable = (extract: IExtract) => {
        const separeItems = (items: OrderItemExtract[]) => {
            const itemsSelectable: ITransferItemSelectable[] = [];
            items.forEach(item => {
                if ((!item.isCanceled && !item.isTransfered)) {
                    if (item.quantity === 1  || item.productType === ProductType.Kilo) {
                        itemsSelectable.push({ ...item, isChecked: false });
                    } else {

                        for (let i = 0; i < item.quantity; i++) {
                            itemsSelectable.push({ ...item, isChecked: false });
                        }
                    }
                }
            });
            return itemsSelectable;
        }

        return extract.orders.filter(item =>
            !item.isPayment &&
            !item.isCanceled &&
            !item.isTransfered &&
            !item.items.every(item => item.isCanceled))
            .map(order => ({ ...order, items: separeItems(order.items) }))
    };
    
    useEffect(() => {
        setSelectableOrder(extractToSelectable(extract));
    }, [extract]);
    
    useEffect(() => {
        if (currentStep === TransferItemsStep.Table) {
            getLocalTableList();
        }
    }, [currentStep, getLocalTableList]);

    const quantity = useMemo(() => selectableOrder.flatMap(item => item.items).filter(item => item.isChecked).length, [selectableOrder]);

    const onChangeHandle = useCallback((item: ITransferItemsFormValue, checked: boolean) => {
        setSelectableOrder(prev => prev.map(order =>
            order.id === item.id ?
                { ...order, items: order.items.map(orderItem => ({ ...orderItem, isChecked: checked })) }
                : order
        ));
    }, []);

    const onChangeItemHandle = useCallback((item: ITransferItemSelectable, checked: boolean) => {
        setSelectableOrder(prev => {
            const newValue = prev.map(order => (
                {
                    ...order, items: order.items.map(orderItem => orderItem.id === item.id ?
                        { ...orderItem, isChecked: checked }
                        :
                        orderItem)
                }
            ))
            return newValue
        })
    }, []);

    const onChangeTableHandle = useCallback((table: ITable) => {
        setSelectedTable(table);
    }, []);

    const onSubmitHandle = useCallback(async () => {
        try {
            await onSubmit(selectableOrder.flatMap(order => order.items.filter(item => item.isChecked)), selectedTable?.id ?? "", observation);
            onClose();
        } catch (error) {
            console.log(error);
        }
    }, [observation, onClose, onSubmit, selectableOrder, selectedTable]);

    const itemsList = useMemo(() => selectableOrder?.map(item => (
        <TransferItemsFormItem
            key={item.id}
            onChange={onChangeHandle}
            onChangeItem={onChangeItemHandle}
            selectableOrder={item}
        />
    )), [onChangeHandle, onChangeItemHandle, selectableOrder]);

    const tableList = useMemo(() => getTableList?.map(item => {
        if (item.id === currentTable) return null;
        if (!!item.startClosingAt || !!item.closingStartedAt) return null;

        return (
            <div key={item.id}>
                <TransferItemsFormTable
                    table={item}
                    onChange={onChangeTableHandle}
                    selectedTable={selectedTable}
                />
            </div>
        )
    }), [currentTable, getTableList, onChangeTableHandle, selectedTable]);

    return (
            <ModalLayout onClose={onClose} title={t('modals.transferOrder.title')}>
            <div id={styles.TransferItemsForm}>
                {currentStep === TransferItemsStep.Items && (
                    <>
                        {itemsList}
                        {/* <div ref={divRef}>
                            <ObservationInput error={inputError} onChange={handleChangeObservation} onFocusInput={scrollInput} />
                        </div> */}
                    </>
                )}

                {currentStep === TransferItemsStep.Table && (
                    <>
                        {isLoading && <CircularProgress size={18} />}
                        {!isLoading && tableList}
                    </>
                )}

                <div className={styles.buttons} >
                    <Button onClick={() => currentStep === TransferItemsStep.Items ? onClose() : setCurrentStep(TransferItemsStep.Items)} className={styles.button} variant='outlined'>{t('modals.transferOrder.back')}</Button>
                    <Button
                        onClick={() => currentStep === TransferItemsStep.Items ? setCurrentStep(TransferItemsStep.Table) : onSubmitHandle()}
                        className={styles.button}
                        variant='contained'
                        disabled={!(hasPermission(OperatorPermissions.TRANSFER_ITEMS) && !!currentOperator) || (currentStep === TransferItemsStep.Items ? !quantity : !selectedTable)}
                    >
                        {currentStep === TransferItemsStep.Items ? t('modals.transferOrder.confirm') : t('modals.transferOrder.transferItems')}
                    </Button>
                </div>
            </div>
        </ModalLayout>
    )
}

export default TransferItemsForm;
